import { Component, inject, OnDestroy } from '@angular/core';
import { HOME_PAGE_FLEXIPAY_URL, ROUTE as PORTAL_ROUTE } from '@portal/data-access';
import { ROUTE as BIZFLEX_ROUTE } from '@bizflex/data-access';
import { UserIdleService } from '@core/user-idle';
import { filter, Subject, switchMap, take, takeUntil } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ContentDialogData, ErrorDialogComponent } from '@portal/dialog';

@Component({
  selector: 'bizflex-root',
  template: '<router-outlet></router-outlet>',
  styles: [':host {@apply block;}'],
})
export class AppComponent implements OnDestroy {
  private readonly idleService = inject(UserIdleService);
  private readonly destroy$ = new Subject<void>();
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);

  constructor() {
    this.idleService.startWatching();

    this.idleService
      .onTimerStart()
      .pipe(
        // Filter all the timer start events
        filter(Boolean),
        take(1),
        switchMap(() =>
          this.dialog
            .open<ErrorDialogComponent, ContentDialogData>(ErrorDialogComponent, {
              panelClass: ['w-10/12', '!max-w-md'],
              data: { title: '', dialogContent: 'sessionExpired', button: { close: { hide: true } } },
            })
            .afterClosed()
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        const url = HOME_PAGE_FLEXIPAY_URL;

        const currentRaw = this.router.url || '';
        const currentPath = currentRaw.split('?')[0].split('#')[0];
        const normalizedCurrent = currentPath.startsWith('/') ? currentPath : `/${currentPath}`;

        // Check query params for utm_source=lead_reminder and redirect to external URL if present
        const queryString = (currentRaw.split('?')[1] || '').split('#')[0];

        try {
          const params = new URLSearchParams(queryString);
          if (params.get('utm_source') === 'lead_reminder') {
            window.location.href = url;
            return;
          }
        } catch (e) {
          // If URLSearchParams fails for some reason, fall back to simple string check
          if (queryString.includes('utm_source=lead_reminder')) {
            window.location.href = url;
            return;
          }
        }

        const leadRoute = BIZFLEX_ROUTE.LEAD.MAIN || '';
        const normalizedRoute = leadRoute.startsWith('/') ? leadRoute : `/${leadRoute}`;

        if (normalizedCurrent.startsWith(normalizedRoute)) {
          window.location.href = url;
          return;
        }

        this.router
          .navigate([PORTAL_ROUTE.VERIFICATION.MAIN])
          .then(() => this.dialog.closeAll())
          .catch(() => this.dialog.closeAll());
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.idleService.stopWatching();
  }
}
